/**
 * Sagelyn design tokens — single source for documentation and future programmatic themes.
 * Runtime styling uses CSS variables in index.css (light / html.dark).
 */

export const THEME_STORAGE_KEY = "app-theme";

/** Preference values stored in localStorage and optional `ui_theme_preference` on the user record */
export const THEME_MODES = ["light", "dark", "system"] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

export type ResolvedAppearance = "light" | "dark";

/** Stable id for the default product theme (not hardcoding mode names in UI logic) */
export const DEFAULT_THEME_PALETTE_ID = "sagelyn" as const;
export type ThemePaletteId = typeof DEFAULT_THEME_PALETTE_ID | string;

/**
 * Extracted from Sagelyn mark: wordmark sage, olive icon disc, ribbon gold, charcoal-green depth, cream paper.
 * System (OS dark) uses `darkSystem`; Light / system+light use `light`.
 */
export const SAGELYN_PALETTE = {
  primary: "#7DAAA7",
  secondary: "#83B1AB",
  accentCool: "#8CAD9A",
  accentWarm: "#C9A86C",
  charcoal: "#2C3A35",
  iconCircle: "#4F6F66",
  cream: "#F7F5F0",
  darkSystem: {
    background: "#242D2A",
    surface: "#2F3835",
    border: "#3D4542",
    textPrimary: "#E8EBE9",
    textSecondary: "#9EADA8",
  },
  light: {
    background: "#F7F5F0",
    surface: "#EFEDE8",
    border: "#E0DDD4",
    textPrimary: "#2C3A35",
    textSecondary: "#5C6B66",
  },
} as const;

export type SagelynContrastMode = "color" | "monochrome";

/** Always use Sagelyn dark palette from CSS variables (`html.dark`); do not force monochrome. */
export function sagelynContrastForThemePreference(_mode: ThemeMode): SagelynContrastMode {
  return "color";
}

/** Clears any legacy `data-sagelyn-contrast` so `html.dark` tokens apply (see index.css). */
export function applySagelynContrastToDocument(mode: SagelynContrastMode): void {
  const root = document.documentElement;
  if (mode === "monochrome") {
    root.dataset.sagelynContrast = "monochrome";
  } else {
    delete root.dataset.sagelynContrast;
  }
}

export function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === "string" && (THEME_MODES as readonly string[]).includes(value);
}

/** Single MediaQueryList for system theme (keep one instance for listeners + .matches). */
export function getSystemColorSchemeMql(): MediaQueryList | null {
  if (typeof window === "undefined" || !window.matchMedia) return null;
  return window.matchMedia("(prefers-color-scheme: dark)");
}

export function resolveAppearance(mode: ThemeMode): ResolvedAppearance {
  if (mode === "system") {
    const mql = getSystemColorSchemeMql();
    if (!mql) return "light";
    return mql.matches ? "dark" : "light";
  }
  return mode;
}

export function readStoredThemeMode(): ThemeMode {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemeMode(raw)) return raw;
  } catch {
    /* ignore */
  }
  return "system";
}

export function applyAppearanceClass(mode: ThemeMode): ResolvedAppearance {
  const root = document.documentElement;
  root.dataset.themePreference = mode;

  if (mode === "light") {
    root.classList.remove("dark");
    root.style.colorScheme = "light";
    root.dataset.themeAppearance = "light";
    return "light";
  }

  if (mode === "dark") {
    root.classList.add("dark");
    root.style.colorScheme = "dark";
    root.dataset.themeAppearance = "dark";
    return "dark";
  }

  // system: never set .dark — rely on CSS @media (prefers-color-scheme)
  root.classList.remove("dark");
  const resolved = resolveAppearance("system");
  root.dataset.themeAppearance = resolved;
  root.style.colorScheme = resolved === "dark" ? "dark" : "light";
  return resolved;
}
